import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DocumentValuesContext,
  MobileAppValuesContext,
  NetworkStatusValuesContext,
} from 'contexts';
import { getLocalSettingsProxy } from 'utilities';

const IDLE_TIME = 45 * 60 * 1000; // Idle for 45 mins

/**
 * This hook checks for idle time and surfaces idle message when idle limit has been reached.
 * It uses localStorage to synchronize activity tracking across multiple browser tabs.
 * @param {Number} idleTimeLimit - time limit in ms before surfacing idle message.
 * @param {Function} onLimit - function to call when idle time has reached the limit
 */
const useIdleMonitor = (onLimit = () => {}, idleTimeLimit = IDLE_TIME) => {
  const [localSettings] = useState(getLocalSettingsProxy);

  const [idleTimeReached, setIdleTimeReached] = useState(false);

  const timeOutRef = useRef();
  const checkIntervalRef = useRef();

  const { frameDocument } = useContext(DocumentValuesContext);
  const { isMobileApp } = useContext(MobileAppValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);

  // no idle monitor if mobileApp or if disabled in localSettings
  const shouldNotTrackIdleTime = useMemo(
    () => isMobileApp || isOffline || localSettings.disableIdleMonitor,
    [isMobileApp, isOffline, localSettings.disableIdleMonitor],
  );

  /**
   * Update the most recent activity timestamp in localStorage
   */
  const updateActivityTimestamp = useCallback(() => {
    const now = Date.now();
    localSettings.sessionTracking.mostRecentActivityTimestamp = now;
  }, [localSettings]);

  /**
   * Check if idle time limit has been reached based on localStorage timestamp
   */
  const checkIdleStatus = useCallback(() => {
    const lastActivity =
      localSettings.sessionTracking.mostRecentActivityTimestamp;

    if (!lastActivity) {
      return false;
    }

    const timeSinceLastActivity = Date.now() - lastActivity;
    return timeSinceLastActivity >= idleTimeLimit;
  }, [localSettings, idleTimeLimit]);

  /**
   * Reset idleTimeReached flag and update activity timestamp to restart timer
   */
  const restartTimer = useCallback(() => {
    updateActivityTimestamp();
    setIdleTimeReached(false);
  }, [updateActivityTimestamp]);

  /**
   * Callback when idle time limit is reached
   */
  const onIdleTimeLimit = useCallback(() => {
    onLimit();
    setIdleTimeReached(true);
  }, [onLimit]);

  /**
   * Clear timers
   */
  const clear = useCallback(() => {
    if (timeOutRef.current) {
      clearTimeout(timeOutRef.current);
      timeOutRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  /**
   * Reset timeout and update activity timestamp
   */
  const reset = useCallback(() => {
    updateActivityTimestamp();
    clear();

    // Set up a new timeout
    timeOutRef.current = setTimeout(() => {
      if (checkIdleStatus()) {
        onIdleTimeLimit();
      }
    }, idleTimeLimit);
  }, [
    updateActivityTimestamp,
    clear,
    idleTimeLimit,
    checkIdleStatus,
    onIdleTimeLimit,
  ]);

  /**
   * Handle window focus - check if we should show dialog based on shared state
   */
  const handleWindowFocus = useCallback(() => {
    // When tab gains focus, check if idle time was reached
    if (checkIdleStatus()) {
      onIdleTimeLimit();
    } else {
      // Reset the timer with the latest activity
      reset();
    }
  }, [checkIdleStatus, onIdleTimeLimit, reset]);

  /**
   * Handle window blur - just update activity timestamp
   */
  const handleWindowBlur = useCallback(() => {
    updateActivityTimestamp();
  }, [updateActivityTimestamp]);

  /**
   * Attach event listeners to reset the timer
   */
  const attachListeners = useCallback(() => {
    window.addEventListener('load', reset);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('mousedown', reset);
    window.addEventListener('scroll', reset, true);
    window.addEventListener('mousemove', reset);
    window.addEventListener('keydown', reset);
    frameDocument?.addEventListener('mousemove', reset);
    frameDocument?.addEventListener('keydown', reset);
  }, [frameDocument, reset, handleWindowFocus, handleWindowBlur]);

  /**
   * Remove event listeners for cleanup
   */
  const removeListeners = useCallback(() => {
    window.removeEventListener('load', reset);
    window.removeEventListener('focus', handleWindowFocus);
    window.removeEventListener('blur', handleWindowBlur);
    window.removeEventListener('mousedown', reset);
    window.removeEventListener('scroll', reset, true);
    window.removeEventListener('mousemove', reset);
    window.removeEventListener('keydown', reset);
    frameDocument?.removeEventListener('mousemove', reset);
    frameDocument?.removeEventListener('keydown', reset);
  }, [frameDocument, reset, handleWindowFocus, handleWindowBlur]);

  /**
   * Periodically check idle status to catch changes from other tabs
   */
  useEffect(() => {
    if (shouldNotTrackIdleTime || idleTimeReached) {
      return;
    }

    // Check every 5 seconds if we should trigger the idle dialog
    checkIntervalRef.current = setInterval(() => {
      if (checkIdleStatus()) {
        onIdleTimeLimit();
      }
    }, 5000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [
    shouldNotTrackIdleTime,
    idleTimeReached,
    checkIdleStatus,
    onIdleTimeLimit,
  ]);

  /**
   * Clear timer and remove listeners if idle time limit is reached.
   * We don't need to reset timer or listen to events once idle time limit is reached.
   * option to set "idleTimeReached" to "false" would be in the idle message which would trigger the timer cycle again
   */
  useEffect(() => {
    if (shouldNotTrackIdleTime) {
      return;
    }

    if (!idleTimeReached) {
      return;
    }

    clear();
    removeListeners();
  }, [clear, idleTimeReached, removeListeners, shouldNotTrackIdleTime]);

  /**
   * Attach listeners and start timer to track if idle or active.
   */
  useEffect(() => {
    if (shouldNotTrackIdleTime) {
      return;
    }

    if (idleTimeReached) {
      return;
    }

    // Initialize activity timestamp if not set
    if (!localSettings.sessionTracking.mostRecentActivityTimestamp) {
      updateActivityTimestamp();
    }

    attachListeners();
    reset();

    return () => {
      removeListeners();
      clear();
    };
  }, [
    idleTimeReached,
    shouldNotTrackIdleTime,
    localSettings,
    updateActivityTimestamp,
    attachListeners,
    reset,
    removeListeners,
    clear,
  ]);

  return restartTimer;
};

export default useIdleMonitor;
